import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type PODisclaimerLabel } from '@webapp/modules/purchasing/entities/PODisclaimerLabel'
import { useMutateDefaultPODisclaimer } from '@webapp/modules/purchasing/features/set-default-po-disclaimer/models'
import messages from './messages'

/**
 * Star toggle for the default disclaimer. Direction detection lives here:
 * a non-default label saves its id (BHV-023); the current default clears
 * with `0` (BHV-024). Per-direction toasts include the label description.
 */
export const useSetDefaultPODisclaimerPresenter = () => {
  const intl = useProcurifyIntl()
  const { mutateAsync, isLoading } = useMutateDefaultPODisclaimer()

  const toggleDefault = async (
    label: PODisclaimerLabel,
    currentDefaultId: number | null
  ) => {
    const isClearing = label.id === currentDefaultId

    try {
      await mutateAsync(isClearing ? 0 : label.id)

      toast.success({
        title: intl.formatMessage(messages.successTitle),
        children: intl.formatMessage(
          isClearing ? messages.removedAsDefault : messages.savedAsDefault,
          { description: label.description }
        ),
      })
    } catch (error) {
      const errorMessage =
        getMessageFromError(error) ||
        intl.formatMessage(messages.defaultErrorMessage)

      toast.error({
        title: intl.formatMessage(messages.errorTitle),
        children: errorMessage,
      })
    }
  }

  return {
    commands: {
      toggleDefault,
    },
    states: {
      isSaving: isLoading,
    },
  }
}
